import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog } from '@/shared/ui';

import styles from './ImportChooser.module.css';

/** One thing that can be imported: what it makes and which files it takes. */
export interface ImportChoice {
  id: string;
  title: string;
  hint: string;
  icon: ReactNode;
  onSelect: () => void;
}

/**
 * The ONE "Import" entry of the Create menu: pick what the file becomes, then the dialog of that
 * import opens. Only what the viewer can actually import here is listed.
 */
export function ImportChooser({
  choices,
  onClose,
}: {
  choices: readonly ImportChoice[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog title={t('import.title')} onClose={onClose}>
      <p className={styles.lead}>{t('import.lead')}</p>
      <ul className={styles.list} data-testid="import-chooser">
        {choices.map((choice) => (
          <li key={choice.id}>
            <button
              type="button"
              className={styles.choice}
              data-testid={`import-${choice.id}`}
              onClick={() => {
                onClose();
                choice.onSelect();
              }}
            >
              <span className={styles.icon} aria-hidden>
                {choice.icon}
              </span>
              <span className={styles.text}>
                <span className={styles.name}>{choice.title}</span>
                <span className={styles.hint}>{choice.hint}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}
